import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { Check, ChevronDown, LayoutGrid } from 'lucide-react';
import { apiAssetSrc } from '../../lib/apiAsset';
import { pickTextColour } from '../../lib/adColours';

/**
 * The club name in the navbar (Stuart, 30 Oct 2026): with another club to go
 * to, tapping it opens a list of the person's own clubs; picking one opens
 * that club's hub (it lands on What's on). Sponsor logins open their Scan
 * page, as My Clubs does. "All my clubs" → My Clubs. With nowhere else to go
 * it stays the plain link to the hub. Crests come from GET /me/club-logos,
 * asked for when the list first opens (sign-in data carries no logos).
 * A disclosure of links (not an ARIA menu): Tab moves through it, Escape or
 * a tap outside closes it.
 */
export const switcherClubs = (memberships) => (memberships || []).filter(m => !m.implicit);

const HEX = /^#[0-9a-f]{6}$/i;

const Crest = ({ name, logo, colour }) => {
  const [broken, setBroken] = useState(false);
  if (logo && !broken) {
    return <img src={apiAssetSrc(logo)} alt="" className="navbar-club-menu-crest" onError={() => setBroken(true)} />;
  }
  // The club's colour as the disc, the letter in charcoal or white —
  // whichever reads better on it (never club colour as text, F-008).
  const bg = HEX.test(colour || '') ? colour : '#1E4A35';
  return (
    <span className="navbar-club-menu-crest navbar-club-menu-initial" style={{ background: bg, color: pickTextColour(bg).text }} aria-hidden="true">
      {name?.charAt(0)?.toUpperCase() || '?'}
    </span>
  );
};

const ClubSwitcher = ({ club, clubSlug, memberships, userId, onOpen, children }) => {
  const [open, setOpen] = useState(false);
  const [asked, setAsked] = useState(false);
  const wrapRef = useRef(null);
  const location = useLocation();

  const { data: logos = {} } = useQuery({
    queryKey: ['my-club-logos', userId],
    queryFn: () => axios.get('/me/club-logos').then(r => r.data?.logos || {}),
    enabled: asked && !!userId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // A new page closes it (the picked club's hub, or anywhere else).
  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onPress = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPress);
    document.addEventListener('touchstart', onPress);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPress);
      document.removeEventListener('touchstart', onPress);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    setAsked(true);
    if (!open && onOpen) onOpen(); // closes the account menu / ☰ sheet
    setOpen(!open);
  };

  const mine = switcherClubs(memberships);
  // Looking round a club that isn't one of yours: it heads the list, so the
  // name still leads back to its hub.
  const visiting = club && !mine.some(m => m.clubSlug === clubSlug)
    ? [{ clubSlug, clubName: club.name, primaryColor: club.primary_color, visiting: true }]
    : [];
  const rows = [...visiting, ...mine];

  return (
    <div className="navbar-club-switch" ref={wrapRef}>
      <button type="button" className="navbar-club-brand navbar-club-trigger" onClick={toggle}
        aria-expanded={open} aria-controls="navbar-club-menu" data-testid="club-switcher">
        {children}
        <ChevronDown size={16} className={`navbar-club-chev${open ? ' open' : ''}`} aria-hidden="true" />
        <span className="navbar-sr"> — change club</span>
      </button>
      {open && (
        <nav id="navbar-club-menu" className="navbar-club-menu" aria-label="Your clubs">
          <div className="navbar-club-menu-label" aria-hidden="true">Your clubs</div>
          <ul className="navbar-club-menu-list">
            {rows.map(m => {
              const here = m.clubSlug === clubSlug;
              const to = m.role === 'sponsor' ? `/${m.clubSlug}/scan` : `/${m.clubSlug}`;
              const logo = here && club?.logo_url ? club.logo_url : logos[m.clubSlug];
              const note = m.visiting ? 'Visiting' : m.clubSuspended ? 'Paused' : m.role === 'sponsor' ? 'Sponsor login' : null;
              return (
                <li key={m.clubSlug}>
                  <Link to={to} className={`navbar-club-menu-item${here ? ' is-here' : ''}`}
                    aria-current={here ? 'page' : undefined}
                    onClick={() => setOpen(false)}>
                    <Crest name={m.clubName} logo={logo} colour={m.primaryColor} />
                    <span className="navbar-club-menu-text">
                      <span className="navbar-club-menu-name">{m.clubName}</span>
                      {note && <span className="navbar-club-menu-note">{note}</span>}
                    </span>
                    {here && <Check size={16} className="navbar-club-menu-tick" aria-hidden="true" />}
                  </Link>
                </li>
              );
            })}
            <li>
              <Link to="/my-clubs" className="navbar-club-menu-item navbar-club-menu-all" onClick={() => setOpen(false)}>
                <LayoutGrid size={16} aria-hidden="true" /> All my clubs
              </Link>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default ClubSwitcher;
